import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import BenchlingConfig from 'BenchlingConfig';
import {Storable, StorableSummary} from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {Selector} from 'notebook/ELNConstants';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import ManageSelectorsModalClass from 'notebook/plain-tables/selectors/ManageSelectorsModal';
import excess from 'notebook/tables/excess';
import {FullTableMap, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {TableOpOutput} from 'notebook/tables/excess/ops/TableOps';
import * as SpreadsheetActions from 'notebook/tables/spreadsheets/SpreadsheetActions';
import SpreadsheetEvalCache from 'notebook/tables/spreadsheets/SpreadsheetEvalCache';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  isTextEditableType,
  MultiSelectionBox,
  TableData,
  TableOpFunction,
  TableOpOptions,
  TypedValue,
} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue, isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {getStore} from 'redux-stores/Store';
import StorableStore from 'samples/StorableStore';

import {CellInsertMode, TryInsertOptions} from '../excess/FormulaUtil';
import {NoteRuntimeSnapshot} from '../TableStoreTransforms';

export interface SpreadsheetEvalStoreState {
  elnResolution: object;
  workflows: object;
  fileSummariesById: {[id: string]: BioentitySummary};
  storablesById: {[id: string]: Storable};
  storableSummariesById: {[id: string]: StorableSummary};
}

function _didStoreDependenciesChange(
  storeState: SpreadsheetEvalStoreState,
  storeDependencies: StoreDependencyMap,
  newFileSummariesByIdReduxState: {[id: string]: BioentitySummary},
  newStorablesById: {[id: string]: Storable},
  newStorableSummariesById: {[id: string]: StorableSummary}
): boolean {
  const bioentityDependencies = storeDependencies.bioentityIds ? storeDependencies.bioentityIds : [];
  for (const bioentityId of bioentityDependencies) {
    if (newFileSummariesByIdReduxState[bioentityId] !== storeState.fileSummariesById[bioentityId]) {
      return true;
    }
  }

  const storableDependencies = storeDependencies.storableIds ? storeDependencies.storableIds : [];
  for (const storableId of storableDependencies) {
    if (newStorablesById[storableId] !== storeState.storablesById[storableId]) {
      return true;
    }
    if (newStorableSummariesById[storableId] !== storeState.storableSummariesById[storableId]) {
      return true;
    }
  }

  return false;
}

function getStorablesByIdFromStore(storableIds: Array<string>): {
  storablesById: {[id: string]: Storable};
  storableSummariesById: {[id: string]: StorableSummary};
} {
  const storablesById: {[id: string]: Storable} = {};
  const storableSummariesById: {[id: string]: StorableSummary} = {};
  for (const storableId of storableIds) {
    const storable = StorableStore.getStorable(storableId);
    const storableSummary = StorableStore.getStorableSummary(storableId);
    if (storable) {
      storablesById[storableId] = storable;
    }
    if (storableSummary) {
      storableSummariesById[storableId] = storableSummary;
    }
  }
  return {storablesById, storableSummariesById};
}

export function updateIfStoreDependenciesChanged({
  storeState,
  storeDependencies,
  tempFullTableMap,
  handleChangeTempFullTableMap,
  makeFullTableMap,
  selectorsFromProps,
  updateEvaluatedTableState,
  fileId,
  isNoteLinkedGroup,
}: {
  storeState: SpreadsheetEvalStoreState;
  storeDependencies: StoreDependencyMap;
  tempFullTableMap: FullTableMap;
  handleChangeTempFullTableMap: (newTempFullTableMap: FullTableMap) => void;
  makeFullTableMap: () => FullTableMap;
  selectorsFromProps: () => Array<Selector>;
  updateEvaluatedTableState: (
    fullTableMap: FullTableMap,
    selectors: Array<Selector>,
    noteRuntimeSnapshot: NoteRuntimeSnapshot | null
  ) => void;
  fileId: string | null;
  isNoteLinkedGroup: boolean;
}): SpreadsheetEvalStoreState {
  const newElnResolutionReduxState = getStore().getState().models.eln.resolutionResultsByResolutionQuery;
  const newWorkflowsReduxState = getStore().getState().models.workflows;
  const newFileSummariesByIdReduxState = getStore().getState().models.fileSummariesById;
  const {storablesById: newStorablesById, storableSummariesById: newStorableSummariesById} =
    getStorablesByIdFromStore(storeDependencies.storableIds || []);
  const isWorkflowReduxStateChanged = storeState.workflows !== newWorkflowsReduxState;
  let fullTableMap = tempFullTableMap;
  // TODO(BNCH-13229): Clean up legacy stage run logic
  if (isWorkflowReduxStateChanged) {
    // TODO(BNCH-5281): We need to recalculate the fullTableMap when workflows change in order to
    // update registration table default rows based on stage runs. This would not be the case if
    // default rows were persisted in registration tables.
    fullTableMap = makeFullTableMap();
    handleChangeTempFullTableMap(makeFullTableMap());
  }
  if (
    storeState.elnResolution !== newElnResolutionReduxState ||
    isWorkflowReduxStateChanged ||
    _didStoreDependenciesChange(
      storeState,
      storeDependencies,
      newFileSummariesByIdReduxState,
      newStorablesById,
      newStorableSummariesById
    )
  ) {
    // NoteLinkedGroups should trigger evaluation specifically for themselves
    if (BenchlingConfig.ELN_ENABLE_RUNTIME_SNAPSHOT_EVAL && fileId && !isNoteLinkedGroup) {
      // Let the entry store know to force an evaluation that may have updated due to store changes
      EntryActions.triggerEntryEvaluation(fileId);
    } else {
      updateEvaluatedTableState(fullTableMap, selectorsFromProps(), null);
    }
  }
  return {
    elnResolution: newElnResolutionReduxState,
    workflows: newWorkflowsReduxState,
    fileSummariesById: newFileSummariesByIdReduxState,
    storablesById: newStorablesById,
    storableSummariesById: newStorableSummariesById,
  };
}

// Evaluation without a NoteRuntimeSnapshot / Resolver
export function evaluateTables({
  fullTableMap,
  selectors,
  oldComputedTables,
  hasFormulasDisabled,
  spreadsheetEvalCacheKey,
  disallowCellRefs,
  fileId,
}: {
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  oldComputedTables: OutputTableMap | null;
  hasFormulasDisabled: boolean;
  spreadsheetEvalCacheKey: string | null;
  disallowCellRefs?: boolean;
  fileId?: string | null;
}): {computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}} {
  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    fileId,
    parse: hasFormulasDisabled
      ? excess.parser.parseAsValue
      : disallowCellRefs
        ? excess.parser.parseWithoutCellRefs
        : excess.parse,
  });
  const recomputedTables = evaluator.output();
  if (spreadsheetEvalCacheKey) {
    SpreadsheetEvalCache.setValues(spreadsheetEvalCacheKey, fullTableMap, recomputedTables);
  }
  const computedTables = _.mapObject(recomputedTables, (currentTable, currentTableName) =>
    // Instead of directly using the tables of evaluator, we optimize each new table by re-using
    // subcomponents of the old table wherever possible, e.g. if we modify a single cell, typically
    // every row of a table will be unchanged. This allows the React component to avoid re-rendering
    // with PureRenderMixin.
    excess.util.cloneWithReuse({
      base: oldComputedTables != null ? oldComputedTables[currentTableName] : undefined,
      source: currentTable,
    })
  );
  return {
    computedTables,
    selectorCounts: getSelectorCounts(fullTableMap),
  };
}

// Synchronous Evaluation without a NoteRuntimeSnapshot / Resolver
export async function evaluateTablesWithResolutions({
  fullTableMap,
  selectors,
  hasFormulasDisabled,
  disallowCellRefs,
}: {
  fullTableMap: FullTableMap;
  selectors: Array<Selector>;
  hasFormulasDisabled: boolean;
  disallowCellRefs?: boolean;
}): Promise<{computedTables: OutputTableMap; selectorCounts: {[selectorId: number]: number}}> {
  const evaluator = new excess.SpreadsheetEvaluator({
    fullTableMap,
    selectors,
    parse: hasFormulasDisabled
      ? excess.parser.parseAsValue
      : disallowCellRefs
        ? excess.parser.parseWithoutCellRefs
        : excess.parse,
  });
  const computedTables = await evaluator.outputWithStringsResolved();

  return {
    computedTables,
    selectorCounts: getSelectorCounts(fullTableMap),
  };
}

export function getSelectorCounts(fullTableMap: FullTableMap): {[selectorId: number]: number} {
  // HACK(T7258): construct a list of items that ManageSelectorsModalClass.countSelectors understands.
  // eslint-disable-next-line no-restricted-syntax
  const tableItems = _.values(fullTableMap).map((table) => ({table}));
  return ManageSelectorsModalClass.countSelectors(tableItems);
}

export function getSpreadsheetStoreState(
  tableNamespace: string,
  hasFormulasDisabled: boolean,
  tableName?: string
): {
  cellColors: {[tableName: string]: {[rowIndex: number]: {[columnIndex: number]: string}}};
  editingTable: string | null;
  showExcessLabels: boolean;
} {
  let showExcessLabels;
  const storeState = SpreadsheetStore.getState(tableNamespace, tableName);
  let cellColors = storeState.cellColors;
  const {editingTable, formula} = storeState;
  if (hasFormulasDisabled) {
    showExcessLabels = false;
    cellColors = {};
  } else {
    showExcessLabels = formula == null || excess.util.getFormulaText(formula) != null;
  }
  return {cellColors, editingTable, showExcessLabels};
}

export function setMultiEditingForTable(
  tableName: string,
  tableNamespace: string,
  {
    editing,
    text,
    typedValue,
    selection,
  }: {
    editing: boolean;
    text: string;
    typedValue: TypedValue;
    selection: {cid: string; start: number; end: number} | null;
  }
): void {
  const hasTypedValue = typedValue != null && !isTypedValueEmpty(typedValue);
  SpreadsheetActions.setFormula({
    tableNamespace,
    tableName,
    formula: text || (isTextEditableType(typedValue) ? formatTypedValue(typedValue) : ''),
    typedValue: hasTypedValue ? typedValue : null,
    editing,
    selection,
  });
}

export function tryInsert({
  tableName,
  e,
  hasFormulasDisabled,
  tableNamespace,
  editingTable,
}: {
  tableName: string;
  e: TryInsertOptions;
  hasFormulasDisabled: boolean;
  tableNamespace: string;
  editingTable: string | null;
}): boolean {
  if (hasFormulasDisabled) {
    return false;
  }
  // We read formula directly from the store since we intentionally avoid introducing it into the state
  // as it is not necessary for rendering purposes (and would otherwise introduce perf issues).
  const {formula: originalFormula} = SpreadsheetStore.getState(tableNamespace);
  const insertion = excess.FormulaUtil.tryInsert(tableName, editingTable, originalFormula, e);
  if (insertion == null) {
    return false;
  }
  const {formula, selection} = insertion;
  SpreadsheetActions.setFormula({
    tableNamespace,
    tableName: editingTable,
    formula,
    typedValue: null,
    editing: true,
    selection,
    cellInsertMode: CellInsertMode.ENABLE,
  });
  return true;
}

export function canInsert({
  e,
  hasFormulasDisabled,
  tableNamespace,
  editingTable,
}: {
  e: {start: number; end: number; cid: string; shift: boolean};
  hasFormulasDisabled: boolean;
  tableNamespace: string;
  editingTable: string | null;
}): boolean {
  if (hasFormulasDisabled) {
    return false;
  }
  const {formula} = SpreadsheetStore.getState(tableNamespace);
  return excess.FormulaUtil.canInsert(editingTable, formula, e);
}

/**
 * Performs an operation on a table, updating the appropriate react components
 * @param  {Number} index   The index of the targetItem containing the table with targetTableName
 * @param  {Function} op    (originalTableMap, computedTables, targetTableName) ->
 *                          {tables: newTableMap, box}, where
 *                            originalTableMap {Object}  Maps original names of tables to original tables.
 *                            targetTableName {String}   The name of the table being directly modified
 *                            newTableMap {Object}       Maps *original* names of tables to new tables.
 *                            box {Object}               {minI, maxI, minJ, maxJ}, or null if no selection
 *                                                       is specified.
 */
export function performTableOp({
  index,
  op,
  options,
  spreadsheetTypes,
  computedTables,
  getItems,
  getRoot,
  getType,
  getCid,
  getContent,
  setContent,
  requestSelection,
  updateRoot,
  itemsAccessor,
}: {
  index: number;
  op: TableOpFunction;
  options?: TableOpOptions;
  spreadsheetTypes: Array<string>;
  computedTables: OutputTableMap;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getItems: (root: any) => Array<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRoot: () => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getType: (item: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getCid: (item: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getContent: (item: any) => {table: TableData};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setContent: (type: string, content: object) => Spec<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  requestSelection: (data: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateRoot: (root: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  itemsAccessor: (op: {[i: number]: Spec<any>}) => Spec<any>;
}): void {
  let table;
  const items = getItems(getRoot());
  const targetItem = items[index];
  const targetTableName = getContent(targetItem).table.name;

  // Apply the operation to retrieve the new table map, along with the desired selection.
  const oldTables: FullTableMap = {};
  for (const item of items) {
    if (spreadsheetTypes.includes(getType(item))) {
      ({table} = getContent(item));
      oldTables[table.name] = table;
    }
  }

  const {tables: newTables, box} = _performTableOpWithGuards({
    tableMap: oldTables,
    computedTables,
    targetTableName,
    op,
    options,
  });

  // For each table that has changed, register an update for the corresponding note part.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const itemUpdates: {[i: number]: Spec<any>} = {};
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (spreadsheetTypes.includes(getType(item))) {
      ({table} = getContent(item));
      const newTable = newTables[table.name];
      if (newTable === table) {
        continue;
      }
      if (TableUtil.isInvalidOpWithWarn(table, newTable, options)) {
        return;
      }
      itemUpdates[i] = setContent(getType(item), {table: newTables[table.name]});
    }
  }

  // Update note parts and set selection
  // The multi selection must be updated (`@props.requestSelection`) before
  // calling `@updateRoot` to prevent the table updating with the currently
  // stale multi selection.
  if (box != null) {
    requestSelection({
      multiSelectKey: TableUtil.getTableMultiSelectKey(getCid(targetItem)),
      data: {
        multiActive: false,
        multiSelIndex: [box.minI, box.minJ],
        multiStartIndex: [box.minI, box.minJ],
        multiEndIndex: [box.maxI, box.maxJ],
        multiActualIndex: [box.minI, box.minJ],
      },
    });
  }
  // TS gets confused by the reactUpdate types, so just use any for now.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateRoot(reactUpdate(getRoot(), itemsAccessor(itemUpdates) as any));
}

interface PerformTableOpsWithGuardsParams {
  tableMap: FullTableMap;
  computedTables: OutputTableMap;
  targetTableName: string;
  op: TableOpFunction;
  options?: TableOpOptions;
}

/** @package Exported only for testing, use performTableOp instead.
 *
 * Performs a given table operation with any additional surrounding actions needed to perform the operation
 * correctly. For example, we sometimes need to unmerge single input columns before performing an operation.
 */
export function _performTableOpWithGuards({
  tableMap: oldTables,
  computedTables,
  targetTableName,
  op,
  options,
}: PerformTableOpsWithGuardsParams): TableOpOutput {
  // If not otherwise specified, before applying the main table operation, unmerge all single inputs in the
  // target table. We still keep them marked as single inputs because some operations (like delete row) behave
  // differently on single inputs.
  const remakeSingleInputs =
    BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_COLUMN_SINGLE_INPUTS && Boolean(options?.remakeSingleInputs);
  const originalSingleInputBoundaryIndex = remakeSingleInputs
    ? TableUtil.findSingleInputBoundaryIndex(oldTables[targetTableName])
    : 0;
  if (remakeSingleInputs && originalSingleInputBoundaryIndex) {
    const targetTable = oldTables[targetTableName];
    const numRows = targetTable.table.length;
    const selection: MultiSelectionBox = {
      minI: 0,
      maxI: numRows - 1,
      minJ: 0,
      maxJ: originalSingleInputBoundaryIndex - 1,
    };
    oldTables = excess.ops.unmergeRegion(oldTables, computedTables, targetTableName, selection).tables;
  }

  // Perform the main table operation
  const tableOpResult = op(oldTables, computedTables, targetTableName);
  let newTables = tableOpResult.tables;
  // Remerge all single input columns afterward if we unmerged them before the operation
  if (remakeSingleInputs && originalSingleInputBoundaryIndex) {
    const newSingleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(newTables[targetTableName]);
    newTables = excess.ops.mergeIntoSingleInput(
      newTables,
      computedTables,
      targetTableName,
      0,
      newSingleInputBoundaryIndex - 1
    ).tables;
  }

  return {tables: newTables, box: tableOpResult.box};
}
